import { ReactNode } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useOAuthCallback } from '@/hooks/useOAuthCallback'

interface ProtectedRouteProps {
  children: ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, status, error, refreshSession } = useAuth()

  // Process OAuth fragment immediately so protected routes can hydrate.
  useOAuthCallback()

  // Check if we're processing an OAuth callback (fragment contains accessToken)
  const hasOAuthFragment =
    typeof window !== 'undefined' &&
    window.location.hash.includes('accessToken=')

  // Show loading spinner while checking authentication or processing OAuth callback
  if (
    status === 'loading' ||
    status === 'hydrating' ||
    (hasOAuthFragment && !isAuthenticated)
  ) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">
            Checking authentication...
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please wait while we verify your login status
          </p>
        </div>
      </div>
    )
  }

  // Show error state if there's an authentication error
  if (error && status === 'ready') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="max-w-md rounded-lg bg-white p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900">
            Authentication Error
          </h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <div className="mt-6 flex gap-4">
            <button
              onClick={() => refreshSession()}
              className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Retry
            </button>
            <Link
              to="/"
              className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // If user is not authenticated and no OAuth callback is pending, redirect to login
  if (!isAuthenticated && !hasOAuthFragment) {
    return <Navigate to="/" replace />
  }

  // If authenticated, render the protected content
  return <>{children}</>
}
